import uk from "./locales/uk.json";
import fi from "./locales/fi.json";
import ru from "./locales/ru.json";
import en from "./locales/en.json";
import { operationMessages, operationMessageIds } from "./ops-messages";
import { getPreference, setPreference, getPreferenceScope } from '../features/guide/preferences';
export type Locale = "en" | "uk" | "fi" | "ru";
export const supportedLocales: Locale[] = ["en", "uk", "fi", "ru"];
const sessionLocales = new Map<string, Locale>();
export function getLocale(): Locale {
  const sessionLocale=sessionLocales.get(getPreferenceScope());
  if (sessionLocale) return sessionLocale;
  try {
    const value =
      typeof localStorage === "undefined"
        ? null
        : getPreference("luuta-language");
    return supportedLocales.includes(value as Locale)
      ? (value as Locale)
      : "en";
  } catch {
    return "en";
  }
}
export function setLocale(locale: Locale) {
  sessionLocales.set(getPreferenceScope(), locale);
  try {
    setPreference("luuta-language", locale);
  } catch {
    /* The selected language still works for this session. */
  }
}
const catalogs: Record<Locale, Record<string, string>> = { en, uk, fi, ru };
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Prefer specific messages over broad patterns such as 'Add {0} {1}'.
const patterns = [
  ...Object.keys(uk)
    .filter((key) => !key.startsWith("ops.") && /\{\d+\}/.test(key))
    .map((key) => ({ key, source: key })),
  ...Object.entries(operationMessages)
    .filter(([, source]) => /\{\d+\}/.test(source))
    .map(([key, source]) => ({ key, source })),
]
  .sort(
    (a, b) =>
      b.source.replace(/\{\d+\}/g, "").length -
      a.source.replace(/\{\d+\}/g, "").length,
  )
  .map(({ key, source }) => ({
    key,
    regex: new RegExp(
      "^" +
        source
          .split(/(\{\d+\})/)
          .map((part) => (/^\{\d+\}$/.test(part) ? "(.*?)" : escape(part)))
          .join("") +
        "$",
    ),
  }));
/** Translate presentation copy only; domain enums, saved data and user-entered names remain stable. */
export function translate(value: string, locale: Locale, depth = 0): string {
  if (!value || depth > 4) return value;
  const messageId = operationMessages[value]
    ? value
    : operationMessageIds[value];
  if (locale === "en")
    return messageId
      ? (catalogs.en[messageId] ?? operationMessages[messageId] ?? value)
      : value;
  const catalog = catalogs[locale];
  if (catalog[value]) return catalog[value];
  if (messageId && catalog[messageId]) return catalog[messageId];
  const trimmed = value.trim();
  if (catalog[trimmed]) return value.replace(trimmed, catalog[trimmed]);
  const trimmedId = operationMessageIds[trimmed];
  if (trimmedId && catalog[trimmedId])
    return value.replace(trimmed, catalog[trimmedId]);
  for (const { key, regex } of patterns) {
    const match = trimmed.match(regex);
    if (match && catalog[key])
      return value.replace(
        trimmed,
        catalog[key].replace(/\{(\d+)\}/g, (_, index) =>
          translate(match[Number(index) + 1], locale, depth + 1),
        ),
      );
  }
  // Compound UI values (roles, skills, readiness notes) retain their separators and custom text.
  return value
    .split(/( · | \+ |\n|(?<=\.) )/)
    .map((part) => catalog[part] ?? catalog[operationMessageIds[part]] ?? part)
    .join("");
}
export function tx<T>(value: T): T {
  return (
    typeof value === "string" ? translate(value, getLocale()) : value
  ) as T;
}

/** Stable-ID messages support named variables without changing persisted domain values. */
export function message(
  id: string,
  variables: Record<string, string | number> = {},
  locale: Locale = getLocale(),
): string {
  const source = translate(id, locale);
  return source.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name: string) =>
    name in variables
      ? typeof variables[name] === "number"
        ? new Intl.NumberFormat(locale).format(variables[name] as number)
        : String(variables[name])
      : match,
  );
}
export function plural(
  id: string,
  count: number,
  locale: Locale = getLocale(),
): string {
  const category = new Intl.PluralRules(locale).select(count);
  const key = `${id}.${category}`;
  return message(
    catalogs[locale][key] || operationMessages[key] ? key : `${id}.other`,
    { count },
    locale,
  );
}
export function formatDuration(
  minutes: number,
  locale: Locale = getLocale(),
): string {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const remainder = safe % 60;
  const part = (value: number, unit: "hour" | "minute") =>
    new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      unitDisplay: "short",
      maximumFractionDigits: 0,
    }).format(value);
  return [
    hours ? part(hours, "hour") : "",
    remainder || !hours ? part(remainder, "minute") : "",
  ]
    .filter(Boolean)
    .join(" ");
}
